'use client';

/**
 * ObeyaCostPlanReviewPlanChangesCard — the Concertina's third body
 * section: `mcp_rev_diff`'s `plan_changes` — which plan-level config
 * field (margin, warranty risk rate, any of the 5 fx rates) moved
 * since tagging. The plan-level pinning gap named in
 * mcp_engine_concept.md/mcp_scenarios_diff.md: `tag_rev` pinned lines
 * and selections but not the plan's resolved commercial config, so a
 * tagged Rev's margin silently read as whatever margin is CURRENT if
 * it changed later — same reproducibility gap the export-Rev work
 * closed one level down, now closed here too.
 *
 * No $ impact — nothing in the read path computes a sell price from
 * margin yet (mcp_engine_concept.md's plan-level-parameters section),
 * so this shows the raw field values moving, never a derived dollar
 * figure — same "don't fake a number that isn't there" posture
 * `-selections-card.tsx` already takes for its own missing $ column.
 *
 * `margin`/`warranty_risk_rate` render as percentages (stored as a
 * fraction, e.g. 0.20 — see PlanParametersMixin's own field
 * description); the 5 fx fields render as plain rates. `null` renders
 * as "—" (unset), not 0 — a real distinction (no override at all vs.
 * an override of zero) `load_costplan_parameters`'s own COALESCE
 * already preserves server-side.
 *
 * Shares ONE `mcp_rev_diff` round-trip with the sibling header/groups/
 * selections cards via React Query dedup on (strategy, params).
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — obeya_costplan_review_plan_changes
 * @see klyde_edge/klyde_intelligence/projects/queries/rev.py — rev_config_diff
 * @see klyde_ipe/components/canvas/cards/obeya/costplan-review-selections-card.tsx — sibling body card (selection changes)
 *
 * @component_role: Card
 * @component_type: Overlay Body Card
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useMcpRevDiff, type McpRevConfigChange } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCostPlanCompareStore } from '@/lib/stores/costplan-compare-store';

const PERCENT_FIELDS = new Set(['margin', 'warranty_risk_rate']);

function formatConfigValue(field: McpRevConfigChange['field'], value: number | null): string {
  if (value === null) return '—';
  return PERCENT_FIELDS.has(field) ? `${(value * 100).toFixed(1)}%` : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function ConfigChangeRow({ pc, L }: { pc: McpRevConfigChange; L: Record<string, string> }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border">
      <span className={cn(text.title, 'truncate max-w-[40%]')}>{L[`plan_field_${pc.field}`] ?? pc.field}</span>
      <span className={cn(text.muted, 'line-through')}>{formatConfigValue(pc.field, pc.pinned_value)}</span>
      <span className={text.muted}>&rarr;</span>
      <span className={text.title}>{formatConfigValue(pc.field, pc.comparison_value)}</span>
    </div>
  );
}

export function ObeyaCostPlanReviewPlanChangesCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const lang = useLanguageStore((s) => s.lang);

  const compareRevId = useCostPlanCompareStore((s) => s.compareRevId);
  const compareMcpId = useCostPlanCompareStore((s) => s.compareMcpId);
  const compareAgainstRevId = useCostPlanCompareStore((s) => s.compareAgainstRevId);
  const effectiveRevId = compareMcpId && mcpId && compareMcpId === mcpId ? compareRevId : null;
  const effectiveAgainstRevId = effectiveRevId ? compareAgainstRevId : null;

  const diffStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rev_diff'));
  const { data: raw, isLoading } = useMcpRevDiff(diffStrategy, mcpId ?? null, effectiveRevId, effectiveAgainstRevId, lang ?? undefined);
  const diff = raw?.data;
  const L = diff?.labels ?? {};
  const planChanges = diff?.plan_changes ?? [];

  const queryKey = ['strategy-data', diffStrategy, { semantic_id: mcpId ?? '', rev_sid: effectiveRevId ?? '', ...(effectiveAgainstRevId ? { compare_rev_sid: effectiveAgainstRevId } : {}), ...(lang ? { lang } : {}) }] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_review_plan_changes',
    surface: 'overlay' as const,
    status: (effectiveRevId ? (diff ? 'loaded' : 'loading') : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: effectiveRevId && diffStrategy ? queryKey : null,
    text: planChanges.length > 0 ? `${planChanges.length} ${L.plan_changes_header ?? ''}` : (L.no_plan_changes ?? ''),
    json: diff ? { plan_changes: diff.plan_changes } : null,
  }), [contentKey, effectiveRevId, diff, diffStrategy, queryKey, planChanges.length, L.plan_changes_header, L.no_plan_changes]));

  if (!effectiveRevId) return null;

  return (
    <div className="pt-2">
      <div className={cn(text.eyebrow, 'px-3 pb-1.5')}>{L.plan_changes_header ?? ''}</div>
      {isLoading ? (
        <div className={cn(text.muted, 'px-3 py-2')}>&hellip;</div>
      ) : planChanges.length === 0 ? (
        <div className={cn(text.muted, 'px-3 py-2')}>{L.no_plan_changes ?? ''}</div>
      ) : (
        <div className="flex flex-col gap-1.5 px-3">
          {planChanges.map((pc) => (
            <ConfigChangeRow key={pc.field} pc={pc} L={L} />
          ))}
        </div>
      )}
    </div>
  );
}
